// Certificate.tsx - Optimized for printing and screenshots
import { memo } from "react";
import { Award, Calendar, GraduationCap, CheckCircle2 } from "lucide-react";

interface CertificateProps {
  title: string;
  studentName: string;
  percentage: number;
  grade?: string;
  topicIcon?: string;
  certificateId?: string;
  issueDate?: string;
  correctAnswers?: number;
  totalQuestions?: number;
}

export const Certificate = memo(({ 
  title,
  studentName,
  percentage,
  grade = "",
  topicIcon = "🏆",
  certificateId = `CERT-${Date.now()}`,
  issueDate = new Date().toISOString(),
  correctAnswers = 0,
  totalQuestions = 0,
}: CertificateProps) => {
  const formattedDate = new Date(issueDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });

  const getScoreColor = (score: number): string => {
    if (score >= 85) return "text-emerald-600";
    if (score >= 70) return "text-blue-600";
    if (score >= 50) return "text-amber-600";
    return "text-rose-600";
  };

  return (
    <div className="w-full max-w-[420px] mx-auto print:max-w-full">
      <div className="relative border-[6px] border-double rounded-2xl p-6 bg-gradient-to-br from-amber-50/80 via-yellow-50/80 to-white shadow-xl print:shadow-none print:border-4">
        
        {/* Decorative corner elements */}
        <div className="absolute top-3 right-4 text-2xl opacity-20">✦</div>
        <div className="absolute bottom-3 left-4 text-2xl opacity-20">✦</div>
        
        <div className="relative z-10 text-center">
          {/* Header */}
          <div className="mb-3">
            <div className="flex items-center justify-center gap-2">
              <div className="h-px w-10 bg-gradient-to-r from-transparent to-amber-300"></div>
              <span className="text-[10px] font-bold tracking-[0.25em] text-amber-600 uppercase">
                Certificate
              </span>
              <div className="h-px w-10 bg-gradient-to-l from-transparent to-amber-300"></div>
            </div>
          </div>

          {/* Award Icon */}
          <div className="relative inline-block mb-3">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-md">
              <span className="text-4xl">{topicIcon}</span>
            </div>
            <div className="absolute -top-0.5 -right-0.5 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
              <CheckCircle2 className="w-3.5 h-3.5 text-white" />
            </div>
          </div>

          {/* Student Name */}
          <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent mb-1 leading-tight">
            {studentName}
          </h2>

          {/* Grade Badge */}
          {grade && (
            <div className="inline-flex items-center gap-1 px-3 py-0.5 bg-indigo-50 border border-indigo-200 rounded-full mb-2">
              <GraduationCap className="w-3 h-3 text-indigo-600" />
              <span className="text-[10px] font-semibold text-indigo-700">
                Grade {grade}
              </span>
            </div>
          )}

          {/* Description */}
          <p className="text-[10px] text-gray-500 mb-1">
            successfully completed
          </p>
          
          <h3 className="text-sm font-bold text-gray-800 mb-2 leading-tight">
            {title}
          </h3>

          {/* Score */}
          <div className={`text-3xl font-bold ${getScoreColor(percentage)} mb-2`}>
            {percentage}%
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-6 text-[10px] text-gray-500 mb-3">
            <div className="flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5 text-amber-500" />
              <span>{correctAnswers}/{totalQuestions} correct</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-amber-500" />
              <span>{formattedDate}</span>
            </div>
          </div>

          {/* Certificate ID */}
          <div className="text-[8px] text-gray-400 mb-3">
            #{certificateId}
          </div>

          {/* Signature */}
          <div className="flex items-center justify-center gap-8 pt-2 border-t border-gray-200">
            <div className="text-center">
              <div className="w-20 h-7 border-b border-gray-300"></div>
              <p className="text-[7px] text-gray-400 mt-0.5">Instructor</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-7 border-b border-gray-300"></div>
              <p className="text-[7px] text-gray-400 mt-0.5">Date</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-2 pt-1.5 border-t border-gray-100">
            <p className="text-[6px] text-gray-400 tracking-wide">
              Verified • Islamic Learning Platform
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

Certificate.displayName = "Certificate";